/**
 * loadGoogleConsentMode — GA4 tag bootstrap with Google Consent Mode v2 (AISEO-GA4-001).
 *
 * Loads `gtag.js` for a measurement ID, but ONLY after installing a Consent Mode v2 default that
 * is region-scoped. This is the piece that makes "track everyone properly, restrict only where
 * law requires it" a real implementation rather than a slogan.
 *
 * ## Why region-scoped defaults, and not a global deny
 *
 * The naive compliant build sets `analytics_storage: 'denied'` for every visitor and waits for a
 * banner click. That is legally safe and analytically destructive: the majority of traffic here is
 * outside the EEA/UK, where no prior-consent requirement applies, and a global deny throws away
 * their data (or forces a cookie wall on people who never needed one) to satisfy a rule that does
 * not govern them. Google's own guidance is the region parameter — deny by default *in the regions
 * that require it*, and grant elsewhere. Same tag, same code path, no reduction in data quality
 * where it is not required.
 *
 * ## The ordering is the correctness property, and it is the easy thing to get wrong
 *
 * `gtag('consent','default',…)` MUST be in the dataLayer BEFORE the gtag.js `<script>` executes.
 * If the tag loads first it fires a page_view under the implicit `granted` default, and the
 * subsequent default call arrives too late to have governed it — one unconsented hit per page load
 * in exactly the regions where that is the violation. This module therefore pushes the default
 * synchronously and only then appends the script, and the test suite asserts that ORDER rather
 * than merely asserting that both happened.
 *
 * ## Denied is not the same as absent
 *
 * Under Consent Mode a denied visitor still sends cookieless pings, which is what lets GA4 model
 * conversions without storing identifiers. So a denied region is not a dark region — do not
 * "helpfully" suppress the tag entirely for EEA traffic; that discards the modelling signal and
 * makes the numbers worse than compliance requires.
 *
 * @module @puffin-core/marketing-shell/analytics/loadGoogleConsentMode
 */

import { readGlobalPrivacyControl } from '../consent/privacySignal.js';

/**
 * Regions that require prior consent before analytics storage.
 *
 * EEA (27) + UK + the EFTA states that adopted the ePrivacy/GDPR regime. Expressed as ISO-3166-1
 * alpha-2 because that is what Google's `region` parameter accepts.
 *
 * ⚠️ This list governs a legal behaviour, so it is a declared constant with a stated basis rather
 * than an inline literal — if a jurisdiction is added, it is added HERE and every consumer moves
 * together. Switzerland (CH) is included deliberately: the revised FADP is consent-adjacent enough
 * that treating it as prior-consent is the defensible default.
 */
export const PRIOR_CONSENT_REGIONS = Object.freeze([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV',
  'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'GB', 'CH',
]);

/** The four Consent Mode v2 signals, denied in prior-consent regions until the visitor acts. */
const GATED_SIGNALS = Object.freeze([
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'analytics_storage',
]);

/** `security_storage` is never gated — it covers fraud prevention and is exempt. */
const DEFAULT_GRANTED = Object.freeze({ security_storage: 'granted' });

const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js';

/**
 * Build the two consent-default calls: a denied default scoped to the prior-consent regions, and
 * a granted default for everywhere else.
 *
 * Both are emitted. Google resolves the most specific matching `region` entry, so the unscoped
 * grant acts as the rest-of-world baseline without overriding the regional deny.
 *
 * A visitor who has OPTED OUT by signal (Global Privacy Control) gets ONE unscoped default with every gated
 * signal denied: the rest-of-world grant is exactly what the signal refuses.
 *
 * @param {string[]} regions
 * @param {boolean} optedOut
 * @returns {Array<['consent','default',Object]>}
 */
function buildConsentDefaults(regions, optedOut) {
  if (optedOut) {
    const everywhere = { ...DEFAULT_GRANTED };
    for (const signal of GATED_SIGNALS) {
      everywhere[signal] = 'denied';
    }
    return [['consent', 'default', everywhere]];
  }
  const denied = { ...DEFAULT_GRANTED, region: [...regions] };
  const granted = { ...DEFAULT_GRANTED };
  for (const signal of GATED_SIGNALS) {
    denied[signal] = 'denied';
    granted[signal] = 'granted';
  }
  return [
    ['consent', 'default', denied],
    ['consent', 'default', granted],
  ];
}

/**
 * @typedef {Object} ConsentModeConfig
 * @property {string} measurementId          GA4 measurement ID (`G-XXXXXXXXXX`).
 * @property {Document} [documentRef]        Injectable document (tests / SSR guards).
 * @property {Object} [windowRef]            Injectable window.
 * @property {string[]} [regions]            Override the prior-consent region list.
 * @property {boolean} [debugMode]           Sets GA4 `debug_mode` for DebugView.
 * @property {boolean} [optedOut]            The visitor's browser sends Global Privacy Control (default: read
 *                                           from the page's navigator). Every gated default is then denied.
 */

/**
 * Install Consent Mode defaults and load gtag.js.
 *
 * Idempotent: a second call for the same measurement ID is a no-op, so a React StrictMode double
 * effect or a client-side route change cannot double-load the tag or re-push defaults.
 *
 * @param {ConsentModeConfig} config
 * @returns {{loaded: boolean, reason?: string}} `loaded:false` with a reason when skipped.
 */
export default function loadGoogleConsentMode(config) {
  if (!config || typeof config !== 'object') {
    throw new TypeError('loadGoogleConsentMode: config (object) is required');
  }
  const {
    measurementId,
    documentRef,
    windowRef,
    regions,
    debugMode = false,
    optedOut = readGlobalPrivacyControl(),
  } = config;

  if (typeof measurementId !== 'string' || !measurementId) {
    throw new TypeError('loadGoogleConsentMode: config.measurementId (string) is required');
  }

  const win = windowRef || (typeof window !== 'undefined' ? window : null);
  const doc = documentRef || (typeof document !== 'undefined' ? document : null);

  // SSR / prerender: the marketing shell renders these pages in Node, where there is no tag to
  // load. Returning a reason rather than throwing keeps the prerender path total — an analytics
  // concern must never be able to fail a page build.
  if (!win || !doc) {
    return { loaded: false, reason: 'no-dom' };
  }

  if (win.__puffinGtagLoaded === measurementId) {
    return { loaded: false, reason: 'already-loaded' };
  }

  win.dataLayer = win.dataLayer || [];
  // gtag MUST push `arguments` — not a spread array. GA reads the arguments object's shape, and a
  // normalised array silently fails to register consent. This is the canonical snippet, verbatim.
  function gtag() {
    win.dataLayer.push(arguments);
  }
  win.gtag = win.gtag || gtag;

  // ORDER-CRITICAL: defaults before the script tag. See the module docblock.
  for (const args of buildConsentDefaults(regions || PRIOR_CONSENT_REGIONS, optedOut)) {
    win.gtag(...args);
  }
  win.gtag('js', new Date());
  win.gtag('config', measurementId, debugMode ? { debug_mode: true } : {});

  const script = doc.createElement('script');
  script.async = true;
  script.src = `${GTAG_SRC}?id=${encodeURIComponent(measurementId)}`;
  doc.head.appendChild(script);

  win.__puffinGtagLoaded = measurementId;
  return { loaded: true };
}

/**
 * Record a visitor's consent decision, updating the live tag.
 *
 * Called by the cookie banner on accept/reject. Only meaningful in prior-consent regions, but safe
 * to call anywhere — an update that grants what is already granted is a no-op to GA.
 *
 * @param {Object} params
 * @param {boolean} params.granted        Whether the visitor accepted analytics/ads storage.
 * @param {Object} [params.windowRef]
 * @returns {boolean} Whether the update was delivered.
 */
export function updateGoogleConsent({ granted, windowRef } = {}) {
  const win = windowRef || (typeof window !== 'undefined' ? window : null);
  if (!win || typeof win.gtag !== 'function') {
    return false;
  }
  const value = granted ? 'granted' : 'denied';
  const payload = {};
  for (const signal of GATED_SIGNALS) {
    payload[signal] = value;
  }
  win.gtag('consent', 'update', payload);
  return true;
}
